import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-block-porn-on-windows',
  category: 'device',
  kicker: 'Windows',
  title: 'How to block porn on Windows 11 and 10 — what holds, what doesn’t',
  seoTitle: 'How to Block Porn on Windows 11 & 10 (For Yourself)',
  description: `Family Safety, hosts file, filtering DNS or an app: how to block porn on a Windows PC for yourself, why each breaks, and the setup that covers PC and phone.`,
  published: '2026-08-25',
  modified: '2026-09-03',
  minutes: 7,
  intro: `A Windows PC is the easiest device to block porn on — and the
          easiest to unblock, because you are the administrator of your own
          machine. This guide covers the four usual methods, what each one
          really holds against, and how to make the PC part of a lock that
          holds instead of the hole in it.`,
  next: ['how-to-block-porn-on-iphone', 'how-to-block-porn-on-mac', 'does-incognito-bypass-porn-blockers'],
  body: (
    <>
      <h2>Method 1 — Microsoft Family Safety</h2>
      <p>
        Windows has parental controls built in. You create a family group,
        add a <b>child account</b>, and turn on web and search filters for
        it. Inappropriate websites are blocked and SafeSearch is forced.
      </p>
      <p>
        The catch is in the sentence: <b>child account</b>. Family Safety
        filters the child, and you are the organiser. Its web filter works
        in Microsoft Edge (other browsers can be blocked outright, which is
        a blunt instrument), and as the adult on the PC you can change any
        of it. If you're locking yourself, you'd be both the parent and the
        child of the same account. That's not a lock, it's a role-play.
      </p>

      <h2>Method 2 — The hosts file</h2>
      <p>
        <code>C:\Windows\System32\drivers\etc\hosts</code>, opened as
        administrator, one domain per line pointed at <code>0.0.0.0</code>.
        Free, instant, and it feels serious.
      </p>
      <p>
        It fails the same two ways it fails on every system: it's a list you
        write, so the next site isn't on it; and you undo it with the same
        Notepad window you used to write it. A hosts file is willpower saved
        as a text file.
      </p>

      <h2>Method 3 — A filtering DNS</h2>
      <p>
        This is the right mechanism. You point the PC's network adapter at a
        resolver that refuses whole <b>categories</b> — pornography, nudity,
        adult themes — for every program on the machine: Chrome, Edge,
        Firefox, private windows, apps with a built-in browser. Nothing on
        the PC is asked to cooperate; the site simply never resolves.
      </p>
      <p>
        And the same weakness as on a Mac: the setting lives in
        <b> Network &amp; Internet → adapter → DNS</b>, a text box you can
        clear, and any VPN client swaps the resolver when it connects. The
        filter is right; the attachment is a sticky note.
      </p>

      <h2>Method 4 — SELFCNTRL for Windows</h2>
      <p>
        The SELFCNTRL app for Windows installs that category filter in a few
        minutes, tied to your account, and adds what a bare DNS setting
        can't: <b>curfews</b> that cut the internet entirely on a schedule
        (11pm to 7am is the usual one), the <b>pace</b> you chose at the
        start — cut it dead, or four locked days a week climbing to seven —
        forced SafeSearch on Google, YouTube and Bing, and the day counter.
        Tightening applies at once; loosening waits twenty-four hours; the
        floor never drops.
      </p>
      <p>
        We'll say it here rather than let you find out: the lock we put
        $100 behind — the profile that physically cannot be removed — is on
        <b> iPhone and iPad</b>, because Apple's supervision gives us a way
        to make a profile immovable. Windows offers nothing equivalent for
        a personal PC. On the PC you get the same filter, the same curfews
        and the same account-level delay. And in practice, the device that
        matters at two in the morning is the phone, not the tower under the
        desk.
      </p>

      <h2>The setup that holds: PC and phone, one account</h2>
      <ol>
        <li><Link to="/register">Create your space</Link> — one email address.</li>
        <li>Lock the iPhone first. Plug it into the PC with a cable, run the SELFCNTRL supervisor, scan the QR code from your account. Ten minutes, once. Full steps in <Link to="/guides/how-to-block-porn-on-iphone">the iPhone guide</Link>.</li>
        <li>Install SELFCNTRL on the PC. A few minutes.</li>
        <li>Set the same night curfew on both.</li>
      </ol>
      <p>
        The point isn't any single device. It's that there is no longer a
        device in the house that isn't behind the same door.
      </p>

      <h2>What it never does</h2>
      <p>
        Nothing is read or logged: category filtering has no history to
        keep. No screenshots, no reports, no partner. The app restricts, it
        doesn't watch. It's spelled out in the <Link to="/confidentialite">privacy policy</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'How do I block porn on Windows 11 for myself, not for a child?',
      a: `Family Safety assumes a parent and a child. For yourself, use a
          category-level DNS filter (free resolvers exist, or the SELFCNTRL
          app), and accept that on a PC where you are the administrator the
          real lock is the one on your phone — that's where the supervised,
          immovable profile goes.`
    },
    {
      q: 'Does a DNS filter block porn in incognito mode?',
      a: `Yes. Incognito changes what the browser remembers, not how the
          machine resolves a domain. The filter answers before the browser
          gets a say. More in the guide on private browsing and VPNs.`
    },
    {
      q: 'Can I block porn on a work laptop?',
      a: `Usually your IT department already does, and you may not have the
          rights to install anything. For a personal PC, the steps above
          apply.`
    }
  ]
}

export default guide
